import { Meeting as MeetingNode, RecurrenceCadence, RecurringMeeting as RecurringMeetingNode } from "@acto/gql/graphql";
import Customer from "./Customer";
import User from "./User";
import Note from "./Note";
import { WithClient } from "./hasClient";
import dayjs, { Dayjs } from "dayjs";

type NextOccurrenceNode = Pick<
  MeetingNode,
  "id" | "scheduledDate" | "occurrenceDate" | "scheduledStartTime" | "scheduledEndTime" | "owner"
>;

/** Series fields as selected by meeting queries; `client` is resolved on the containing Meeting. */
type SeriesNode = Pick<
  RecurringMeetingNode,
  "id" | "cadence" | "startDate" | "until" | "scheduledStartTime" | "scheduledEndTime"
> & { nextOccurrence?: NextOccurrenceNode | null };

type MeetingData = Omit<MeetingNode, "series"> & { series?: SeriesNode | null };

export class SeriesOccurrence {
  constructor(
    public readonly id: string,
    public readonly scheduledDate: Dayjs,
    public readonly occurrenceDate?: Dayjs | null,
    public readonly scheduledStartTime?: string | null,
    public readonly scheduledEndTime?: string | null,
    public readonly owner?: User | null,
  ) {}

  static fromGraphql(data: NextOccurrenceNode): SeriesOccurrence {
    return new SeriesOccurrence(
      data.id,
      dayjs(data.scheduledDate),
      data.occurrenceDate ? dayjs(data.occurrenceDate) : null,
      data.scheduledStartTime ?? null,
      data.scheduledEndTime ?? null,
      data.owner ? User.fromGraphql(data.owner) : null,
    );
  }
}

export class MeetingSeries {
  constructor(
    public readonly id: string,
    public readonly cadence: RecurrenceCadence,
    public readonly startDate: Dayjs,
    public readonly until?: Dayjs | null,
    public readonly scheduledStartTime?: string | null,
    public readonly scheduledEndTime?: string | null,
    public readonly nextOccurrence?: SeriesOccurrence | null,
  ) {}

  static fromGraphql(data: SeriesNode): MeetingSeries {
    return new MeetingSeries(
      data.id,
      data.cadence,
      dayjs(data.startDate),
      data.until ? dayjs(data.until) : null,
      data.scheduledStartTime ?? null,
      data.scheduledEndTime ?? null,
      data.nextOccurrence ? SeriesOccurrence.fromGraphql(data.nextOccurrence) : null,
    );
  }
}

export default class Meeting {
  constructor(
    public readonly id: string,
    public readonly customer: Customer,
    public readonly scheduledDate: Dayjs,
    public readonly note?: Note | null,
    public readonly scheduledStartTime?: string | null,
    public readonly scheduledEndTime?: string | null,
    public readonly series?: MeetingSeries | null,
    /** Immutable series anchor; skip/reschedule target this even after the occurrence moved. */
    public readonly occurrenceDate?: Dayjs | null,
    public readonly owner?: User | null,
  ) {}

  static fromGraphql(data: WithClient<MeetingData>): Meeting {
    // If already transformed, return as-is
    if (data instanceof Meeting) {
      return data;
    }

    return new Meeting(
      data.id,
      Customer.fromGraphql(data.client),
      dayjs.isDayjs(data.scheduledDate) ? data.scheduledDate : dayjs(data.scheduledDate),
      data.note ? Note.fromGraphql(data.note) : null,
      data.scheduledStartTime ?? null,
      data.scheduledEndTime ?? null,
      data.series ? MeetingSeries.fromGraphql(data.series) : null,
      data.occurrenceDate ? dayjs(data.occurrenceDate) : null,
      data.owner ? User.fromGraphql(data.owner) : null,
    );
  }
}
